'use client';

import React, { useState } from 'react';
import { Shield, Lock, FileText, Briefcase, AlertCircle, CheckCircle2, XCircle, Clock, Upload, Plus, ChevronDown, ChevronUp, AlertTriangle, ThumbsUp, ThumbsDown, Info, Paperclip, Save, TrendingDown, Zap, BarChart2, Target } from 'lucide-react';
import { type DDReview } from '@/lib/mockData';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import { useRole } from '@/contexts/RoleContext';


interface Props {
  review: DDReview;
}

const DD_DOMAIN_CONFIGS: Record<
  string,
  {
    icon: React.ElementType;
    color: string;
    checklistItems: Array<{ id: string; label: string; required: boolean }>;
    artifactsRequired: string[];
    description: string;
    riskWeights: Record<string, number>;
    remediations: Array<{ control: string; action: string; priority: 'Immediate' | 'Short-term' | 'Long-term'; effort: 'Low' | 'Medium' | 'High' }>;
  }
> = {
  Cybersecurity: {
    icon: Shield,
    color: 'text-blue-600',
    description: 'Evaluate vendor cybersecurity controls, incident response capability, and independent assessment status.',
    checklistItems: [
      { id: 'cy-01', label: 'Multi-factor authentication enforced on all admin accounts', required: true },
      { id: 'cy-02', label: 'Encryption at rest for all city data', required: true },
      { id: 'cy-03', label: 'Encryption in transit (TLS 1.2+)', required: true },
      { id: 'cy-04', label: 'Centralized logging and SIEM in place', required: true },
      { id: 'cy-05', label: 'Vulnerability management program documented', required: true },
      { id: 'cy-06', label: 'Patch management SLA ≤30 days for critical patches', required: true },
      { id: 'cy-07', label: 'Endpoint detection and response (EDR) deployed', required: false },
      { id: 'cy-08', label: 'Identity and access management (IAM) policy', required: true },
      { id: 'cy-09', label: 'Incident response plan tested within 12 months', required: true },
      { id: 'cy-10', label: 'Independent security assessment (SOC 2 / pen test) current', required: true },
      { id: 'cy-11', label: 'Secure software development lifecycle (SDLC) if applicable', required: false },
      { id: 'cy-12', label: 'Third-party / supply chain risk program', required: false },
    ],
    artifactsRequired: [
      'SOC 2 Type II Report (current year)',
      'Penetration Test Summary (within 12 months)',
      'Security Policy Summary',
      'Incident Response Plan',
      'MFA Policy Documentation',
    ],
    riskWeights: { 'cy-01': 15, 'cy-02': 12, 'cy-03': 10, 'cy-04': 8, 'cy-05': 8, 'cy-06': 10, 'cy-07': 5, 'cy-08': 10, 'cy-09': 10, 'cy-10': 12, 'cy-11': 5, 'cy-12': 5 },
    remediations: [
      { control: 'MFA Enforcement', action: 'Require vendor to enable MFA on all privileged accounts within 30 days and provide screenshot evidence.', priority: 'Immediate', effort: 'Low' },
      { control: 'Patch Management', action: 'Obtain written commitment to patch critical CVEs within 30-day SLA. Schedule follow-up scan in 45 days.', priority: 'Immediate', effort: 'Medium' },
      { control: 'SOC 2 Renewal', action: 'Request current-year SOC 2 Type II report or bridge letter from auditor. Set 60-day deadline.', priority: 'Short-term', effort: 'Low' },
      { control: 'SIEM Log Retention', action: 'Require vendor to update log retention policy to 12 months minimum and provide configuration evidence.', priority: 'Short-term', effort: 'Medium' },
      { control: 'Incident Response Testing', action: 'Request tabletop exercise results or schedule joint IR drill within 90 days.', priority: 'Long-term', effort: 'High' },
    ],
  },
  Privacy: {
    icon: Lock,
    color: 'text-purple-600',
    description: 'Assess vendor data handling practices, privacy controls, and compliance with applicable privacy law.',
    checklistItems: [
      { id: 'pr-01', label: 'Data categories processed documented and limited to stated purpose', required: true },
      { id: 'pr-02', label: 'Purpose limitation clause in contract', required: true },
      { id: 'pr-03', label: 'Data retention schedule defined and aligned to city policy', required: true },
      { id: 'pr-04', label: 'Secure deletion / destruction process documented', required: true },
      { id: 'pr-05', label: 'Data return or destruction on contract end confirmed', required: true },
      { id: 'pr-06', label: 'Breach notification within 72 hours contractually required', required: true },
      { id: 'pr-07', label: 'Public records / disclosure support capability confirmed', required: true },
      { id: 'pr-08', label: 'Subprocessor / data sharing restrictions in place', required: true },
      { id: 'pr-09', label: 'Privacy impact assessment completed if applicable', required: false },
      { id: 'pr-10', label: 'CCPA / CalOPPA compliance confirmed if applicable', required: false },
    ],
    artifactsRequired: [
      'Data Processing Agreement (DPA)',
      'Privacy Policy',
      'Data Retention Schedule',
      'Subprocessor List',
      'Breach Notification Procedure',
    ],
    riskWeights: { 'pr-01': 12, 'pr-02': 12, 'pr-03': 10, 'pr-04': 10, 'pr-05': 10, 'pr-06': 15, 'pr-07': 10, 'pr-08': 12, 'pr-09': 5, 'pr-10': 4 },
    remediations: [
      { control: 'Breach Notification SLA', action: 'Ensure 72-hour breach notification clause is explicitly included in the DPA with liquidated damages.', priority: 'Immediate', effort: 'Low' },
      { control: 'Data Retention Schedule', action: 'Request vendor provide a data retention schedule aligned to city records retention policy within 30 days.', priority: 'Short-term', effort: 'Medium' },
      { control: 'Subprocessor Controls', action: 'Obtain current subprocessor list and verify each subprocessor has equivalent data protection obligations.', priority: 'Short-term', effort: 'Medium' },
      { control: 'Privacy Impact Assessment', action: 'Complete PIA for any processing of sensitive personal data categories before go-live.', priority: 'Long-term', effort: 'High' },
    ],
  },
  Financial: {
    icon: Briefcase,
    color: 'text-emerald-600',
    description: 'Review vendor financial health, stability, and ability to fulfill contractual obligations.',
    checklistItems: [
      { id: 'fi-01', label: 'Audited financial statements reviewed (most recent year)', required: true },
      { id: 'fi-02', label: 'No material adverse events or going-concern disclosures', required: true },
      { id: 'fi-03', label: 'D&B or equivalent credit/risk score reviewed', required: false },
      { id: 'fi-04', label: 'Ownership structure and beneficial ownership confirmed', required: true },
      { id: 'fi-05', label: 'No active debarment or exclusion on SAM.gov / Cal eProcure', required: true },
      { id: 'fi-06', label: 'No conflicts of interest identified', required: true },
      { id: 'fi-07', label: 'Business continuity / key-person risk assessed', required: false },
    ],
    artifactsRequired: [
      'Audited Financial Statements',
      'Vendor Disclosure Form',
      'SAM.gov / Cal eProcure Exclusion Check',
      'D&B Report or Equivalent',
    ],
    riskWeights: { 'fi-01': 20, 'fi-02': 25, 'fi-03': 10, 'fi-04': 15, 'fi-05': 20, 'fi-06': 15, 'fi-07': 5 },
    remediations: [
      { control: 'Financial Statements', action: 'Request most recent audited financial statements. If unavailable, obtain CPA-reviewed statements or bank reference letter.', priority: 'Immediate', effort: 'Low' },
      { control: 'Debarment Check', action: 'Run SAM.gov and Cal eProcure exclusion check. Document results and retain in vendor file.', priority: 'Immediate', effort: 'Low' },
      { control: 'Business Continuity', action: 'Request vendor BCP/DRP documentation and assess key-person dependency risk for critical services.', priority: 'Short-term', effort: 'Medium' },
    ],
  },
  Insurance: {
    icon: FileText,
    color: 'text-amber-600',
    description: 'Verify vendor insurance coverage meets city risk management requirements.',
    checklistItems: [
      { id: 'ins-01', label: 'Certificate of Insurance (COI) received and current', required: true },
      { id: 'ins-02', label: 'Commercial General Liability — minimum limits met', required: true },
      { id: 'ins-03', label: 'Professional Liability / E&O — minimum limits met', required: true },
      { id: 'ins-04', label: 'Cyber Liability — minimum limits met (if applicable)', required: false },
      { id: 'ins-05', label: 'Workers Compensation — confirmed', required: true },
      { id: 'ins-06', label: 'Auto Liability — confirmed if applicable', required: false },
      { id: 'ins-07', label: 'City of Pleasanton named as additional insured', required: true },
      { id: 'ins-08', label: 'COI expiration date tracked with 60-day renewal alert', required: true },
    ],
    artifactsRequired: [
      'Certificate of Insurance (COI)',
      'Additional Insured Endorsement',
      'Umbrella / Excess Policy Confirmation',
    ],
    riskWeights: { 'ins-01': 20, 'ins-02': 18, 'ins-03': 18, 'ins-04': 10, 'ins-05': 12, 'ins-06': 7, 'ins-07': 10, 'ins-08': 5 },
    remediations: [
      { control: 'COI Receipt', action: 'Request current COI from vendor immediately. Do not allow work to commence without valid insurance on file.', priority: 'Immediate', effort: 'Low' },
      { control: 'Additional Insured', action: 'Require vendor to add City of Pleasanton as additional insured on all applicable policies and provide endorsement.', priority: 'Immediate', effort: 'Low' },
      { control: 'Cyber Liability', action: 'For vendors with access to city systems or data, require minimum $1M cyber liability coverage.', priority: 'Short-term', effort: 'Low' },
      { control: 'Renewal Tracking', action: 'Set calendar alerts 60 days before COI expiration. Automate reminder to vendor procurement contact.', priority: 'Long-term', effort: 'Low' },
    ],
  },
  'Security Assessment': {
    icon: Shield,
    color: 'text-indigo-600',
    description: 'Review third-party security assessment results and remediation status.',
    checklistItems: [
      { id: 'sa-01', label: 'SOC 2 Type II report received (current period)', required: true },
      { id: 'sa-02', label: 'No qualified opinions in SOC 2 report', required: true },
      { id: 'sa-03', label: 'Penetration test completed within 12 months', required: true },
      { id: 'sa-04', label: 'Critical findings from last assessment remediated', required: true },
      { id: 'sa-05', label: 'Vulnerability scan results reviewed', required: false },
    ],
    artifactsRequired: [
      'SOC 2 Type II Report',
      'Penetration Test Report',
      'Remediation Evidence',
    ],
    riskWeights: { 'sa-01': 30, 'sa-02': 25, 'sa-03': 25, 'sa-04': 15, 'sa-05': 5 },
    remediations: [
      { control: 'SOC 2 Currency', action: 'Require current-year SOC 2 Type II report. If not available, obtain bridge letter from auditor covering the gap period.', priority: 'Immediate', effort: 'Low' },
      { control: 'Pen Test Recency', action: 'Require penetration test conducted within 12 months by a qualified third party. Scope must include city-facing systems.', priority: 'Short-term', effort: 'High' },
      { control: 'Finding Remediation', action: 'Obtain evidence of remediation for all critical and high findings from prior assessments before approval.', priority: 'Immediate', effort: 'Medium' },
    ],
  },
};

const DEFAULT_CONFIG = {
  icon: FileText,
  color: 'text-zinc-600',
  description: 'Complete the due diligence review for this domain.',
  checklistItems: [
    { id: 'gen-01', label: 'Domain questionnaire completed', required: true },
    { id: 'gen-02', label: 'Required artifacts received and reviewed', required: true },
    { id: 'gen-03', label: 'Findings documented', required: true },
    { id: 'gen-04', label: 'Reviewer sign-off obtained', required: true },
  ],
  artifactsRequired: ['Completed questionnaire', 'Supporting documentation'],
  riskWeights: { 'gen-01': 25, 'gen-02': 25, 'gen-03': 25, 'gen-04': 25 },
  remediations: [
    { control: 'Questionnaire', action: 'Ensure vendor completes all required questionnaire sections before proceeding.', priority: 'Immediate' as const, effort: 'Low' as const },
  ],
};

type CheckState = Record<string, boolean | 'na'>;
type FindingSeverity = 'Critical' | 'High' | 'Moderate' | 'Low';

interface Finding {
  id: string;
  severity: FindingSeverity;
  description: string;
  control: string;
  status: 'Open' | 'Remediation' | 'Accepted' | 'Closed';
  added: string;
}

const MOCK_FINDINGS: Record<string, Finding[]> = {
  'DD-2026-001': [
    { id: 'F-001', severity: 'Critical', description: 'MFA not enforced on administrative accounts with access to city billing data.', control: 'IAM / Access Control', status: 'Remediation', added: '2026-02-14' },
    { id: 'F-002', severity: 'High', description: 'Patch management SLA exceeded for 3 critical CVEs identified in Q1 2026.', control: 'Vulnerability Management', status: 'Open', added: '2026-03-01' },
    { id: 'F-003', severity: 'Moderate', description: 'SIEM log retention policy does not meet city 12-month minimum requirement.', control: 'Logging & Monitoring', status: 'Open', added: '2026-03-10' },
  ],
  'DD-2026-008': [
    { id: 'F-004', severity: 'Critical', description: 'SOC 2 Type II report expired December 2025. Current year report not yet provided.', control: 'Independent Assessment', status: 'Open', added: '2026-03-01' },
    { id: 'F-005', severity: 'High', description: 'Penetration test not conducted within required 12-month window.', control: 'Penetration Testing', status: 'Open', added: '2026-03-01' },
  ],
};

// Domain sub-areas for progress bars
const DOMAIN_AREAS: Record<string, Array<{ label: string; itemIds: string[] }>> = {
  Cybersecurity: [
    { label: 'Access & Identity', itemIds: ['cy-01', 'cy-08'] },
    { label: 'Data Protection', itemIds: ['cy-02', 'cy-03'] },
    { label: 'Monitoring & Response', itemIds: ['cy-04', 'cy-09'] },
    { label: 'Vulnerability Mgmt', itemIds: ['cy-05', 'cy-06', 'cy-07'] },
    { label: 'Assessments', itemIds: ['cy-10', 'cy-11', 'cy-12'] },
  ],
  Privacy: [
    { label: 'Data Governance', itemIds: ['pr-01', 'pr-02', 'pr-03'] },
    { label: 'Data Lifecycle', itemIds: ['pr-04', 'pr-05'] },
    { label: 'Incident & Disclosure', itemIds: ['pr-06', 'pr-07'] },
    { label: 'Third-Party Controls', itemIds: ['pr-08', 'pr-09', 'pr-10'] },
  ],
  Financial: [
    { label: 'Financial Health', itemIds: ['fi-01', 'fi-02', 'fi-03'] },
    { label: 'Ownership & Integrity', itemIds: ['fi-04', 'fi-05', 'fi-06'] },
    { label: 'Business Continuity', itemIds: ['fi-07'] },
  ],
  Insurance: [
    { label: 'Coverage Verification', itemIds: ['ins-01', 'ins-02', 'ins-03'] },
    { label: 'Specialized Coverage', itemIds: ['ins-04', 'ins-05', 'ins-06'] },
    { label: 'City Requirements', itemIds: ['ins-07', 'ins-08'] },
  ],
  'Security Assessment': [
    { label: 'SOC 2 Compliance', itemIds: ['sa-01', 'sa-02'] },
    { label: 'Penetration Testing', itemIds: ['sa-03', 'sa-04'] },
    { label: 'Vulnerability Scans', itemIds: ['sa-05'] },
  ],
};

function calcRiskScore(
  checkStates: CheckState,
  config: typeof DEFAULT_CONFIG,
  findings: Finding[]
): { score: number; label: string; color: string; bgColor: string } {
  const weights = config.riskWeights;
  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
  let passedWeight = 0;
  let failedWeight = 0;

  for (const [id, weight] of Object.entries(weights)) {
    const state = checkStates[id];
    if (state === true) passedWeight += weight;
    else if (state === false) failedWeight += weight;
    // undefined or 'na' = neutral
  }

  // Base score: 100 - (failed weight / total weight * 100)
  let score = Math.round(100 - (failedWeight / totalWeight) * 100);

  // Deduct for open findings
  const criticalCount = findings.filter((f) => f.severity === 'Critical' && f.status !== 'Closed').length;
  const highCount = findings.filter((f) => f.severity === 'High' && f.status !== 'Closed').length;
  score = Math.max(0, score - criticalCount * 15 - highCount * 8);

  if (score >= 80) return { score, label: 'Low Risk', color: 'text-emerald-600', bgColor: 'bg-emerald-500' };
  if (score >= 60) return { score, label: 'Moderate Risk', color: 'text-amber-600', bgColor: 'bg-amber-500' };
  if (score >= 40) return { score, label: 'High Risk', color: 'text-orange-600', bgColor: 'bg-orange-500' };
  return { score, label: 'Critical Risk', color: 'text-red-600', bgColor: 'bg-red-500' };
}

export default function DDReviewPanel({ review }: Props) {
  const config = DD_DOMAIN_CONFIGS[review.domain] || DEFAULT_CONFIG;
  const DomainIcon = config.icon;
  const { can } = useRole();

  const [activeSection, setActiveSection] = useState<'checklist' | 'findings' | 'artifacts' | 'risk-score' | 'decision'>('checklist');
  const [checkStates, setCheckStates] = useState<CheckState>({});
  const [findingNotes, setFindingNotes] = useState('');
  const [residualRisk, setResidualRisk] = useState<string>('');
  const [recommendation, setRecommendation] = useState<string>('');
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());

  const findings = MOCK_FINDINGS[review.id] || [];
  const checkedCount = Object.values(checkStates).filter((v) => v === true).length;
  const totalItems = config.checklistItems.length;
  const completionPct = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  const openFindings = findings.filter((f) => f.status === 'Open' || f.status === 'Remediation');
  const criticalFindings = findings.filter((f) => f.severity === 'Critical' && f.status !== 'Closed');

  const riskScore = calcRiskScore(checkStates, config, findings);
  const domainAreas = DOMAIN_AREAS[review.domain] || [];

  const handleCheck = (id: string, val: boolean | 'na') => {
    setCheckStates((prev) => ({ ...prev, [id]: val }));
  };

  const toggleFinding = (id: string) => {
    setExpandedFindings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    toast.success('Progress saved', { description: `${review.domain} review for ${review.vendorName} updated.` });
  };

  const handleSubmitForApproval = () => {
    if (criticalFindings.length > 0) {
      toast.warning('Unresolved critical findings', { description: `${criticalFindings.length} critical finding(s) must be resolved or formally accepted before approval.` });
      return;
    }
    toast.success('Submitted for approval', { description: `Review submitted to ${review.reviewer} for final sign-off.` });
  };

  const SECTIONS = [
    { key: 'checklist' as const, label: 'Checklist', count: `${checkedCount}/${totalItems}` },
    { key: 'findings' as const, label: 'Findings', count: findings.length > 0 ? String(findings.length) : undefined, alert: criticalFindings.length > 0 },
    { key: 'artifacts' as const, label: 'Artifacts', count: String(config.artifactsRequired.length) },
    { key: 'risk-score' as const, label: 'Risk Score', count: String(riskScore.score) },
    { key: 'decision' as const, label: 'Decision Panel' },
  ];

  return (
    <div className="flex flex-col h-full min-h-0 bg-[hsl(var(--surface))]">
      {/* Panel header */}
      <div className="px-5 py-3.5 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3 flex-shrink-0">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-md bg-[hsl(var(--background))] ${config.color} flex-shrink-0`}>
            <DomainIcon size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-[hsl(var(--text-primary))]">{review.domain} Review</h2>
              <StatusBadge type="dd" value={review.status} />
              <StatusBadge type="risk" value={review.riskTier} />
            </div>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">{review.vendorName}</p>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5 leading-snug max-w-xl">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className="text-2xs text-[hsl(var(--text-muted))]">Due</p>
            <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{review.dueDate}</p>
          </div>
          <div className="text-right">
            <p className="text-2xs text-[hsl(var(--text-muted))]">Reviewer</p>
            <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{review.reviewer}</p>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 py-2 border-b border-[hsl(var(--border))] flex items-center gap-3 flex-shrink-0 bg-[hsl(var(--background))]">
        <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--border))]">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${
              review.status === 'Overdue' ? 'bg-red-500' : completionPct === 100 ? 'bg-emerald-500' : 'bg-teal-500'
            }`}
            style={{ width: `${Math.max(review.completionPct, completionPct)}%` }}
          />
        </div>
        <span className="text-xs font-semibold tabular-nums text-[hsl(var(--text-secondary))] flex-shrink-0">
          {Math.max(review.completionPct, completionPct)}% complete
        </span>
        {findings.length > 0 && (
          <span className="flex items-center gap-1 text-2xs text-amber-600 dark:text-amber-400 flex-shrink-0">
            <AlertTriangle size={11} />
            {findings.length} finding{findings.length > 1 ? 's' : ''}
          </span>
        )}
        {/* Risk score pill */}
        <span className={`flex items-center gap-1 text-2xs font-semibold flex-shrink-0 px-2 py-0.5 rounded-full ${
          riskScore.score >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
          : riskScore.score >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
          : riskScore.score >= 40 ? 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400' :'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
        }`}>
          <Target size={10} />
          Score: {riskScore.score}
        </span>
      </div>

      {/* Section tabs */}
      <div className="flex border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex-shrink-0">
        {SECTIONS.map((sec) => (
          <button
            key={`sec-${sec.key}`}
            onClick={() => setActiveSection(sec.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-all whitespace-nowrap ${
              activeSection === sec.key
                ? 'border-teal-600 text-teal-700 dark:text-teal-400 bg-teal-50/50 dark:bg-teal-950/20' :'border-transparent text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--background))]'
            }`}
          >
            {sec.label}
            {sec.count !== undefined && (
              <span className={`text-2xs px-1.5 py-0.5 rounded-full font-semibold tabular-nums ${
                sec.alert
                  ? 'bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400'
                  : sec.key === 'risk-score'
                  ? riskScore.score >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                    : riskScore.score >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400' :'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' :'bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))]'
              }`}>
                {sec.count}
              </span>
            )}
          </button>
        ))}
        <div className="flex-1" />
        <div className="flex items-center gap-2 px-3">
          <button onClick={handleSave} className="btn-secondary text-xs h-7">
            <Save size={11} />
            Save Progress
          </button>
        </div>
      </div>

      {/* Section content */}
      <div className="flex-1 overflow-y-auto">

        {/* Checklist section */}
        {activeSection === 'checklist' && (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                Control Checklist — {review.domain}
              </h3>
              <span className="text-xs text-[hsl(var(--text-muted))] tabular-nums">
                {checkedCount} of {totalItems} items confirmed
              </span>
            </div>

            {/* Domain area progress bars */}
            {domainAreas.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 p-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                {domainAreas.map((area) => {
                  const areaTotal = area.itemIds.length;
                  const areaDone = area.itemIds.filter((id) => checkStates[id] === true).length;
                  const areaFailed = area.itemIds.filter((id) => checkStates[id] === false).length;
                  const areaPct = areaTotal > 0 ? Math.round((areaDone / areaTotal) * 100) : 0;
                  return (
                    <div key={area.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-2xs font-medium text-[hsl(var(--text-secondary))] truncate">{area.label}</span>
                        <span className="text-2xs tabular-nums text-[hsl(var(--text-muted))] ml-1 flex-shrink-0">{areaDone}/{areaTotal}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                        <div
                          className={`h-1.5 rounded-full transition-all ${
                            areaFailed > 0 ? 'bg-red-500' : areaPct === 100 ? 'bg-emerald-500' : 'bg-teal-500'
                          }`}
                          style={{ width: `${areaPct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-1.5">
              {config.checklistItems.map((item) => {
                const state = checkStates[item.id];
                const weight = (config.riskWeights as Record<string, number>)[item.id] || 0;
                return (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                      state === true
                        ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20'
                        : state === 'na' ?'border-[hsl(var(--border))] bg-zinc-50/60 dark:bg-zinc-900/20 opacity-60'
                        : state === false
                        ? 'border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/20' :'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-[hsl(var(--background))]'
                    }`}
                  >
                    <div className="flex flex-col gap-1 flex-shrink-0 mt-0.5">
                      <button
                        onClick={() => handleCheck(item.id, state === true ? undefined as unknown as boolean : true)}
                        className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${
                          state === true
                            ? 'bg-emerald-600 border-emerald-600 text-white' :'border-[hsl(var(--border-strong))] hover:border-emerald-500'
                        }`}
                        title="Mark as confirmed"
                      >
                        {state === true && <CheckCircle2 size={12} />}
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[hsl(var(--text-primary))] leading-snug">{item.label}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {item.required && <span className="text-2xs text-red-600 dark:text-red-400 font-medium">Required</span>}
                        {!item.required && <span className="text-2xs text-[hsl(var(--text-muted))]">Optional</span>}
                        <span className="text-2xs font-mono text-[hsl(var(--text-muted))]">{item.id}</span>
                        <span className="text-2xs text-[hsl(var(--text-muted))]">Weight: {weight}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleCheck(item.id, true)} title="Confirmed / Pass" className={`p-1.5 rounded transition-colors ${state === true ? 'text-emerald-600 bg-emerald-100 dark:bg-emerald-950' : 'text-[hsl(var(--text-muted))] hover:text-emerald-600 hover:bg-emerald-50'}`}>
                        <CheckCircle2 size={13} />
                      </button>
                      <button onClick={() => handleCheck(item.id, false)} title="Finding / Fail" className={`p-1.5 rounded transition-colors ${state === false ? 'text-red-600 bg-red-100 dark:bg-red-950' : 'text-[hsl(var(--text-muted))] hover:text-red-600 hover:bg-red-50'}`}>
                        <XCircle size={13} />
                      </button>
                      <button onClick={() => handleCheck(item.id, 'na')} title="Not Applicable" className={`p-1.5 rounded transition-colors text-xs font-bold ${state === 'na' ? 'text-zinc-600 bg-zinc-100 dark:bg-zinc-800' : 'text-[hsl(var(--text-muted))] hover:text-zinc-600 hover:bg-zinc-50'}`}>
                        N/A
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Notes field */}
            <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
              <label className="text-xs font-medium text-[hsl(var(--text-secondary))] block mb-1.5">Reviewer Notes</label>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-2">Document observations, context, or exceptions relevant to this domain review.</p>
              <textarea
                value={findingNotes}
                onChange={(e) => setFindingNotes(e.target.value)}
                placeholder="Enter reviewer notes, observations, or contextual information…"
                rows={4}
                className="input-base text-xs w-full resize-none"
              />
            </div>
          </div>
        )}

        {/* Findings section */}
        {activeSection === 'findings' && (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">Findings Log</h3>
              <button onClick={() => toast.info('New finding form coming soon.')} className="btn-primary text-xs">
                <Plus size={11} />
                Add Finding
              </button>
            </div>

            {findings.length === 0 ? (
              <div className="py-12 text-center border border-dashed border-[hsl(var(--border))] rounded-lg">
                <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2 opacity-60" />
                <p className="text-sm font-medium text-[hsl(var(--text-secondary))]">No findings recorded</p>
                <p className="text-xs text-[hsl(var(--text-muted))] mt-1 max-w-xs mx-auto">No control gaps or issues identified for this domain.</p>
                <button onClick={() => toast.info('New finding form coming soon.')} className="btn-secondary text-xs mt-3">
                  <Plus size={11} />
                  Add Finding
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {findings.map((finding) => {
                  const isExpanded = expandedFindings.has(finding.id);
                  return (
                    <div key={finding.id} className={`rounded-md border overflow-hidden ${finding.severity === 'Critical' ? 'border-red-200 dark:border-red-900' : finding.severity === 'High' ? 'border-orange-200 dark:border-orange-900' : 'border-[hsl(var(--border))]'}`}>
                      <button onClick={() => toggleFinding(finding.id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[hsl(var(--background))] transition-colors text-left">
                        <AlertTriangle size={14} className={finding.severity === 'Critical' ? 'text-red-600 flex-shrink-0' : finding.severity === 'High' ? 'text-orange-600 flex-shrink-0' : 'text-amber-600 flex-shrink-0'} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-2xs font-mono text-[hsl(var(--text-muted))]">{finding.id}</span>
                            <StatusBadge type="severity" value={finding.severity} />
                            <span className={`badge text-2xs ${finding.status === 'Open' ? 'badge-pending' : finding.status === 'Remediation' ? 'badge-in-progress' : finding.status === 'Accepted' ? 'badge-moderate' : 'badge-complete'}`}>{finding.status}</span>
                          </div>
                          <p className="text-xs text-[hsl(var(--text-primary))] mt-0.5 leading-snug">{finding.description}</p>
                        </div>
                        {isExpanded ? <ChevronUp size={14} className="text-[hsl(var(--text-muted))] flex-shrink-0" /> : <ChevronDown size={14} className="text-[hsl(var(--text-muted))] flex-shrink-0" />}
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                          <div className="pt-3 grid grid-cols-2 gap-3 text-xs mb-3">
                            <div>
                              <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Control Domain</p>
                              <p className="font-medium text-[hsl(var(--text-primary))]">{finding.control}</p>
                            </div>
                            <div>
                              <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Date Identified</p>
                              <p className="font-medium font-mono text-[hsl(var(--text-primary))]">{finding.added}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-2xs font-medium text-[hsl(var(--text-secondary))] block mb-1">Corrective Action / Remediation Plan</label>
                              <textarea rows={2} placeholder="Describe the required corrective action and timeline…" className="input-base text-xs w-full resize-none" />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => toast.success('Finding updated', { description: `${finding.id} status updated to Remediation.` })} className="btn-secondary text-xs">Mark Remediation</button>
                              <button onClick={() => toast.warning('Risk acceptance requires leadership approval.', { description: 'Accepted risk findings must have documented justification.' })} className="btn-ghost text-xs">Accept Risk</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Artifacts section */}
        {activeSection === 'artifacts' && (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">Required Artifacts</h3>
              {can('dd:upload') && (
                <button onClick={() => toast.info('Document upload coming soon.')} className="btn-primary text-xs">
                  <Upload size={11} />
                  Upload Document
                </button>
              )}
            </div>
            <div className="space-y-2">
              {config.artifactsRequired.map((artifact, i) => {
                const isReceived = i < Math.floor(config.artifactsRequired.length * (review.completionPct / 100));
                return (
                  <div key={`art-${i}`} className={`flex items-center gap-3 p-3 rounded-md border transition-colors ${isReceived ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-[hsl(var(--background))]'}`}>
                    {isReceived ? <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" /> : <Clock size={15} className="text-[hsl(var(--text-muted))] flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{artifact}</p>
                      {isReceived ? (
                        <p className="text-2xs text-emerald-600 dark:text-emerald-400 mt-0.5">Received · Uploaded 2026-03-{10 + i}</p>
                      ) : (
                        <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">Pending receipt</p>
                      )}
                    </div>
                    {isReceived ? (
                      <button onClick={() => toast.info(`Viewing ${artifact}`)} className="btn-ghost text-xs flex-shrink-0"><Paperclip size={11} />View</button>
                    ) : (
                      <button onClick={() => toast.info(`Requesting ${artifact} from vendor.`)} className="btn-secondary text-xs flex-shrink-0">Request</button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="pt-3 border-t border-[hsl(var(--border))]">
              <p className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-2">Artifact Notes</p>
              <textarea rows={3} placeholder="Document any artifact exceptions, waivers, or notes regarding received evidence…" className="input-base text-xs w-full resize-none" />
            </div>
          </div>
        )}

        {/* Risk Score tab */}
        {activeSection === 'risk-score' && (
          <div className="p-5 space-y-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">Risk Score & Remediation Recommendations</h3>

            {/* Score card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1 p-4 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] flex flex-col items-center justify-center text-center">
                <div className="relative w-24 h-24 mb-3">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="hsl(var(--border))" strokeWidth="10" />
                    <circle
                      cx="50" cy="50" r="40" fill="none"
                      stroke={riskScore.score >= 80 ? '#10b981' : riskScore.score >= 60 ? '#f59e0b' : riskScore.score >= 40 ? '#f97316' : '#ef4444'}
                      strokeWidth="10"
                      strokeDasharray={`${(riskScore.score / 100) * 251.2} 251.2`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold tabular-nums text-[hsl(var(--text-primary))]">{riskScore.score}</span>
                    <span className="text-2xs text-[hsl(var(--text-muted))]">/ 100</span>
                  </div>
                </div>
                <p className={`text-sm font-bold ${riskScore.color}`}>{riskScore.label}</p>
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-1">Calculated from checklist responses and open findings</p>
              </div>

              <div className="md:col-span-2 p-4 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] space-y-3">
                <p className="text-xs font-semibold text-[hsl(var(--text-secondary))] uppercase tracking-wide">Domain Area Breakdown</p>
                {domainAreas.length > 0 ? domainAreas.map((area) => {
                  const areaTotal = area.itemIds.length;
                  const areaDone = area.itemIds.filter((id) => checkStates[id] === true).length;
                  const areaFailed = area.itemIds.filter((id) => checkStates[id] === false).length;
                  const areaPct = areaTotal > 0 ? Math.round((areaDone / areaTotal) * 100) : 0;
                  return (
                    <div key={area.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[hsl(var(--text-primary))]">{area.label}</span>
                        <div className="flex items-center gap-2">
                          {areaFailed > 0 && <span className="text-2xs text-red-600">{areaFailed} failed</span>}
                          <span className="text-xs font-semibold tabular-nums text-[hsl(var(--text-secondary))]">{areaPct}%</span>
                        </div>
                      </div>
                      <div className="h-2 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                        <div
                          className={`h-2 rounded-full transition-all ${areaFailed > 0 ? 'bg-red-500' : areaPct === 100 ? 'bg-emerald-500' : 'bg-teal-500'}`}
                          style={{ width: `${areaPct}%` }}
                        />
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-xs text-[hsl(var(--text-muted))]">No domain areas defined for this review type.</p>
                )}
              </div>
            </div>

            {/* Score factors */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Controls Passed', value: Object.values(checkStates).filter((v) => v === true).length, total: totalItems, color: 'text-emerald-600', icon: CheckCircle2 },
                { label: 'Controls Failed', value: Object.values(checkStates).filter((v) => v === false).length, total: totalItems, color: 'text-red-600', icon: XCircle },
                { label: 'Open Findings', value: openFindings.length, total: findings.length, color: 'text-amber-600', icon: AlertTriangle },
              ].map((factor) => (
                <div key={factor.label} className="p-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-center">
                  <factor.icon size={18} className={`mx-auto mb-1.5 ${factor.color}`} />
                  <p className={`text-xl font-bold tabular-nums ${factor.color}`}>{factor.value}</p>
                  <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{factor.label}</p>
                </div>
              ))}
            </div>

            {/* Auto-remediation recommendations */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={14} className="text-teal-600" />
                <h4 className="text-xs font-semibold text-[hsl(var(--text-primary))]">Auto-Remediation Recommendations</h4>
                <span className="text-2xs px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400 font-semibold">{config.remediations.length}</span>
              </div>
              <div className="space-y-2">
                {config.remediations.map((rec, i) => (
                  <div key={i} className={`p-3.5 rounded-md border ${
                    rec.priority === 'Immediate' ? 'border-red-200 bg-red-50/40 dark:border-red-900 dark:bg-red-950/10'
                    : rec.priority === 'Short-term'? 'border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/10' :'border-[hsl(var(--border))] bg-[hsl(var(--background))]'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {rec.priority === 'Immediate' ? (
                          <AlertCircle size={14} className="text-red-600" />
                        ) : rec.priority === 'Short-term' ? (
                          <TrendingDown size={14} className="text-amber-600" />
                        ) : (
                          <BarChart2 size={14} className="text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-semibold text-[hsl(var(--text-primary))]">{rec.control}</span>
                          <span className={`text-2xs px-1.5 py-0.5 rounded-full font-semibold ${
                            rec.priority === 'Immediate' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                            : rec.priority === 'Short-term'? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400' :'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400'
                          }`}>{rec.priority}</span>
                          <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium border ${
                            rec.effort === 'Low' ? 'border-emerald-300 text-emerald-700 dark:text-emerald-400'
                            : rec.effort === 'Medium'? 'border-amber-300 text-amber-700 dark:text-amber-400' :'border-red-300 text-red-700 dark:text-red-400'
                          }`}>{rec.effort} Effort</span>
                        </div>
                        <p className="text-xs text-[hsl(var(--text-secondary))] leading-snug">{rec.action}</p>
                      </div>
                      <button
                        onClick={() => toast.success('Remediation task created', { description: `Task for "${rec.control}" added to action plan.` })}
                        className="btn-secondary text-xs flex-shrink-0"
                      >
                        Create Task
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Decision Panel section */}
        {activeSection === 'decision' && (
          <div className="p-5 space-y-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">Due Diligence Decision Panel</h3>

            {/* Risk assessment summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-1.5 uppercase tracking-wide font-semibold">Inherent Risk</p>
                <StatusBadge type="risk" value={review.riskTier} />
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-2 leading-snug">Based on data sensitivity, system access, and service criticality from intake assessment.</p>
              </div>
              <div className="p-3.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-1.5 uppercase tracking-wide font-semibold">Control Strength</p>
                <div className="flex items-center gap-1.5">
                  {openFindings.length === 0 ? <span className="badge badge-active">Strong</span> : criticalFindings.length > 0 ? <span className="badge badge-critical">Weak</span> : <span className="badge badge-moderate">Moderate</span>}
                </div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-2 leading-snug">{openFindings.length} open finding{openFindings.length !== 1 ? 's' : ''}, {criticalFindings.length} critical</p>
              </div>
              <div className="p-3.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-1.5 uppercase tracking-wide font-semibold">Calculated Risk Score</p>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold tabular-nums ${riskScore.color}`}>{riskScore.score}</span>
                  <span className={`text-xs font-semibold ${riskScore.color}`}>{riskScore.label}</span>
                </div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-2 leading-snug">Weighted score based on control responses and open findings.</p>
              </div>
            </div>

            {/* Reviewer residual risk input */}
            <div className="space-y-3 pt-1">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text-secondary))] block mb-1.5">
                  Reviewer Residual Risk Assessment <span className="text-red-500 ml-0.5">*</span>
                </label>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-2">Confirm the residual risk rating based on findings and control evidence.</p>
                <div className="flex gap-2">
                  {['Critical', 'High', 'Moderate', 'Low'].map((r) => (
                    <button
                      key={`rr-${r}`}
                      onClick={() => setResidualRisk(r)}
                      className={`flex-1 py-2 text-xs font-medium rounded-md border transition-all ${
                        residualRisk === r
                          ? r === 'Critical' ? 'bg-red-600 border-red-600 text-white'
                            : r === 'High' ? 'bg-orange-500 border-orange-500 text-white'
                            : r === 'Moderate'? 'bg-amber-500 border-amber-500 text-white' :'bg-emerald-600 border-emerald-600 text-white' :'bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:border-[hsl(var(--border-strong))]'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[hsl(var(--text-secondary))] block mb-1.5">Findings Summary</label>
                <textarea rows={3} placeholder="Summarize the key findings, control gaps, and evidence reviewed during this assessment…" className="input-base text-xs w-full resize-none" />
              </div>

              <div>
                <label className="text-xs font-medium text-[hsl(var(--text-secondary))] block mb-1.5">
                  Approval Recommendation <span className="text-red-500 ml-0.5">*</span>
                </label>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-2">Select a recommendation based on the overall assessment.</p>
                <div className="space-y-2">
                  {[
                    { key: 'approve', label: 'Approve — No material issues identified', icon: ThumbsUp, color: 'text-emerald-600', selectedColor: 'bg-emerald-50 border-emerald-400 dark:bg-emerald-950/30' },
                    { key: 'approve-conditions', label: 'Approve with Conditions — Issues require remediation within 90 days', icon: Info, color: 'text-amber-600', selectedColor: 'bg-amber-50 border-amber-400 dark:bg-amber-950/30' },
                    { key: 'risk-acceptance', label: 'Approve with Risk Acceptance — Unresolved findings accepted with leadership sign-off', icon: AlertCircle, color: 'text-orange-600', selectedColor: 'bg-orange-50 border-orange-400 dark:bg-orange-950/30' },
                    { key: 'reject', label: 'Reject — Material findings prevent proceeding without remediation', icon: ThumbsDown, color: 'text-red-600', selectedColor: 'bg-red-50 border-red-400 dark:bg-red-950/30' },
                  ].map((opt) => {
                    const OptIcon = opt.icon;
                    const isSelected = recommendation === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setRecommendation(opt.key)}
                        className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-md border text-left transition-all ${isSelected ? opt.selectedColor : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-[hsl(var(--background))]'}`}
                      >
                        <OptIcon size={15} className={`flex-shrink-0 ${isSelected ? opt.color : 'text-[hsl(var(--text-muted))]'}`} />
                        <span className={`text-xs ${isSelected ? 'font-medium text-[hsl(var(--text-primary))]' : 'text-[hsl(var(--text-secondary))]'}`}>{opt.label}</span>
                        {isSelected && <CheckCircle2 size={14} className={`ml-auto flex-shrink-0 ${opt.color}`} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {recommendation === 'risk-acceptance' && (
                <div className="p-3.5 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">Risk Acceptance Required</p>
                      <p className="text-2xs text-amber-700 dark:text-amber-500 mt-0.5 leading-snug">Unresolved high or critical findings require a formal risk acceptance memo signed by the Department Owner and City Manager before the vendor may proceed to contracting.</p>
                    </div>
                  </div>
                  <textarea rows={2} placeholder="Document the business justification for accepting this residual risk…" className="input-base text-xs w-full resize-none border-amber-300 dark:border-amber-800" />
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-2 border-t border-[hsl(var(--border))]">
              {can('dd:approve') && (
                <button onClick={handleSubmitForApproval} disabled={!recommendation || !residualRisk} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
                  <CheckCircle2 size={13} />
                  Submit for Approval
                </button>
              )}
              <button onClick={handleSave} className="btn-secondary"><Save size={13} />Save Draft</button>
              <button onClick={() => toast.info('Escalation workflow coming soon.')} className="btn-ghost"><AlertTriangle size={13} />Escalate</button>
              {(!recommendation || !residualRisk) && can('dd:approve') && (
                <p className="text-2xs text-[hsl(var(--text-muted))] ml-2">Select residual risk and recommendation to submit.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}